// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
//
// The verdict card: three columns, one per carrier, for one region. Every other
// view routes here. Each column reuses the frequency axis from the Spectrum
// ladder, so a shortfall reads as a HOLE AT A FREQUENCY rather than a small
// number in a table.

import type { TowerData, Region, Carrier } from '../types';
import { CARRIER_ALIAS, CARRIER_COLOR, GEN_COLOR, setLabel } from '../colors';
import { attr, esc, num, pct } from '../format';
import { gloss } from '../glossary';
import { viewHead, statRow } from './ui';

export function mount(el: HTMLElement, data: TowerData): void {
  const params = new URLSearchParams(location.hash.replace(/^#/, ''));
  const wanted = params.get('sa3');
  const start = wanted && data.regionByCode.has(wanted) ? data.regionByCode.get(wanted)! : defaultRegion(data);
  let ri = start;

  const sorted = [...data.regions]
    .map((r, i) => ({ r, i }))
    .sort((a, b) => a.r.name.localeCompare(b.r.name));

  el.innerHTML = `
    ${viewHead({
      title: 'Region verdict',
      question: 'In this region, which network should I actually be on — and is it getting better or worse?',
      blurb: `Each column is one carrier's presence in the region, laid out on the same frequency axis used across the site: ${gloss('lowband', 'long reach')} at the bottom, ${gloss('midband', 'capacity')} above it. Each bar is the share of that carrier's sites <em>here</em> carrying the band; the grey diamond is its share nationally. A bar well short of its diamond is a named deficit at a named frequency.`,
    })}
    <div class="ctl-row">
      <label class="sel-lab" for="region-pick">Region</label>
      <select id="region-pick" data-pick>
        ${sorted.map(({ r, i }) => `<option value="${i}"${i === ri ? ' selected' : ''}>${esc(r.name)} — ${esc(r.state)}</option>`).join('')}
      </select>
    </div>
    <div data-body></div>
  `;

  const body = el.querySelector('[data-body]') as HTMLElement;

  function render(): void {
    const r = data.regions[ri];
    body.innerHTML = regionBody(data, r);
    body.querySelectorAll('[data-go]').forEach((b) => {
      b.addEventListener('click', () => {
        location.hash = `view=${b.getAttribute('data-go')}`;
      });
    });
  }

  (el.querySelector('[data-pick]') as HTMLSelectElement).addEventListener('change', (e) => {
    ri = Number((e.target as HTMLSelectElement).value);
    const r = data.regions[ri];
    history.replaceState(null, '', `#view=region&sa3=${r.code}`);
    render();
  });

  render();
}

function defaultRegion(data: TowerData): number {
  // Open on a region where the answer actually matters: the most populated one
  // with only a single real carrier option.
  let best = 0;
  let bestPop = -1;
  data.regions.forEach((r, i) => {
    const only = r.set.split('').filter((c) => c === '1').length === 1;
    if (only && r.pop > bestPop) {
      bestPop = r.pop;
      best = i;
    }
  });
  return bestPop > 0 ? best : 0;
}

function regionBody(data: TowerData, r: Region): string {
  const { meta } = data;
  const carriers = meta.carriers;
  const live = r.set.split('');
  const nationalTotals = meta.totals.byCarrier;

  const verdictText = (() => {
    const options = carriers.filter((_, i) => live[i] === '1');
    if (!options.length) return { tone: 'alert', msg: 'No carrier has meaningful long-reach spectrum recorded in this region.' };
    if (options.length === 1) {
      return {
        tone: 'warn',
        msg: `${CARRIER_ALIAS[options[0]]} is the only network with substantial long-reach spectrum here. On this evidence it is the realistic choice for anyone who needs coverage away from the town centre.`,
      };
    }
    if (options.length === 3) {
      return { tone: 'good', msg: 'All three networks hold substantial long-reach spectrum here — a genuine three-way choice.' };
    }
    return { tone: 'good', msg: `${options.map((c) => CARRIER_ALIAS[c]).join(' and ')} both hold substantial long-reach spectrum here.` };
  })();

  const cols = carriers
    .map((c, ci) => carrierColumn(data, r, c, live[ci] === '1', nationalTotals[c]))
    .join('');

  return `
    ${statRow([
      { label: 'Residents', value: num(r.pop), sub: `ABS ERP ${meta.erpYear}` },
      { label: 'Area', value: `${num(r.areaKm2)} km²`, sub: r.areaCls },
      { label: 'Mobile sites', value: num(r.total), sub: 'all three networks' },
      {
        label: 'Publicly co-funded',
        value: num(r.coFunded),
        sub: r.total ? pct(r.coFunded / r.total, 0) + ' of the region' : '—',
        tone: r.coFunded ? 'warn' : 'plain',
      },
      { label: 'Real options', value: String(live.filter((x) => x === '1').length), sub: setLabel(r.set, carriers) },
    ])}

    <div class="verdict verdict-${verdictText.tone}">
      <p>${esc(verdictText.msg)}</p>
      ${r.mocn ? `<p class="verdict-sub">${num(r.mocn)} sites here are shared Optus–TPG equipment, so TPG customers may be served by towers TPG does not own.</p>` : ''}
    </div>

    <div class="region-cols">${cols}</div>

    <section class="card">
      <div class="card-head"><h2>Sites here since ${data.meta.years[0]}</h2><p>Registered sites per network, by year. A falling line means the carrier reported fewer sites in this region.</p></div>
      ${histChart(data, r)}
    </section>

    <div class="jump-row">
      <button class="btn" data-go="coverage">See this on the map →</button>
      <button class="btn" data-go="address">Measure from a specific spot →</button>
      <button class="btn" data-go="ignored">Compare funding across regions →</button>
    </div>
  `;
}

function carrierColumn(data: TowerData, r: Region, c: Carrier, isOption: boolean, nationalSites: number): string {
  const stats = r.car[c];
  const bands = [...data.meta.bands].sort((a, b) => b.mhz - a.mhz);
  const held = new Set(stats.bands);
  const hist = data.history.carriers[c];

  const W = 300;
  const rowH = 17;
  const P = { t: 8, l: 84, r: 46 };
  const H = P.t + bands.length * rowH + 8;
  const iw = W - P.l - P.r;

  const lanes = bands
    .map((b, i) => {
      const y = P.t + i * rowH;
      const has = held.has(b.id);
      // BOTH values are shares of that carrier's own sites, which is the only
      // way the bar and the diamond can be compared. An earlier version drew
      // the bar full-width for "present" and then invited the reader to compare
      // its length against a national-share diamond — two different scales on
      // one lane.
      const localCount = stats.bandCounts?.[b.id] ?? 0;
      const localShare = stats.sites ? localCount / stats.sites : 0;
      const natCount = hist.bands[b.id][data.history.years.length - 1];
      const natShare = nationalSites ? natCount / nationalSites : 0;
      const dx = P.l + natShare * iw;
      const bw = localShare * iw;
      const tip = has
        ? `${b.id} — ${b.gen} on ${b.mhz >= 1000 ? `${b.mhz / 1000} GHz` : `${b.mhz} MHz`}\nAt ${num(localCount)} of ${CARRIER_ALIAS[c]}'s ${num(stats.sites)} sites in ${r.name} (${pct(localShare)})\nNationally: ${pct(natShare)} of its sites`
        : `${b.id} — not present at any ${CARRIER_ALIAS[c]} site in ${r.name}\nNationally: ${pct(natShare)} of its sites`;
      return `<g data-tip="${attr(tip)}">
        <rect x="${P.l}" y="${y}" width="${iw}" height="${rowH - 4}" fill="var(--bg-elevated)" rx="2" />
        ${bw > 0.4 ? `<rect x="${P.l}" y="${y}" width="${bw.toFixed(1)}" height="${rowH - 4}" fill="${GEN_COLOR[b.gen]}" fill-opacity="0.8" rx="2" />` : ''}
        ${natShare > 0.02 ? `<path d="M${dx.toFixed(1)},${y + 1.5} l4,${(rowH - 7) / 2} l-4,${(rowH - 7) / 2} l-4,-${(rowH - 7) / 2} Z" fill="none" stroke="var(--text-tertiary)" stroke-width="1.2" />` : ''}
        <text x="${P.l - 6}" y="${y + rowH - 8}" class="lad-band" text-anchor="end" fill="${has ? 'var(--text-primary)' : 'var(--text-muted)'}">${esc(b.id)}</text>
      </g>`;
    })
    .join('');

  const chips: string[] = [];
  if (stats.sites === 0) chips.push('<span class="chip-flag bad">No sites at all</span>');
  if (stats.low === 0 && stats.sites > 0) chips.push('<span class="chip-flag bad">No long-reach spectrum</span>');
  if (stats.g5 === 0 && stats.sites > 0) chips.push('<span class="chip-flag warn">No 5G here</span>');
  if (stats.coFunded > 0) chips.push(`<span class="chip-flag">${num(stats.coFunded)} publicly funded</span>`);
  if (isOption && stats.low > 0) chips.push('<span class="chip-flag good">A real option here</span>');

  return `<figure class="region-col ${isOption ? '' : 'region-col-dim'}" style="--c:${CARRIER_COLOR[c]}">
    <figcaption>
      <strong>${esc(CARRIER_ALIAS[c])}</strong>
      <span>${num(stats.sites)} sites · ${num(stats.low)} with long reach · ${num(stats.g5)} with 5G</span>
    </figcaption>
    <div class="chip-flags">${chips.join('')}</div>
    <svg viewBox="0 0 ${W} ${H}" role="img" aria-label="${esc(CARRIER_ALIAS[c])} bands present in ${esc(r.name)}">${lanes}</svg>
    <p class="col-key">bar = share of this carrier's sites <em>here</em> carrying the band · ◇ = its national share</p>
  </figure>`;
}

function histChart(data: TowerData, r: Region): string {
  const years = data.meta.years;
  const W = 900;
  const H = 210;
  const P = { t: 14, r: 16, b: 30, l: 48 };
  const iw = W - P.l - P.r;
  const ih = H - P.t - P.b;
  const max = Math.max(1, ...data.meta.carriers.flatMap((c) => r.hist[c])) * 1.12;
  const x = (i: number) => P.l + (i / (years.length - 1)) * iw;
  const y = (v: number) => P.t + ih - (v / max) * ih;

  const lines = data.meta.carriers
    .map((c) => {
      const vals = r.hist[c];
      const d = vals.map((v, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(' ');
      const dots = vals
        .map(
          (v, i) =>
            `<circle cx="${x(i).toFixed(1)}" cy="${y(v).toFixed(1)}" r="4" fill="#fff" stroke="${CARRIER_COLOR[c]}" stroke-width="2"
              data-tip="${attr(`${years[i]} · ${CARRIER_ALIAS[c]}\n${num(v)} sites in ${r.name}`)}" />`,
        )
        .join('');
      return `<path d="${d}" fill="none" stroke="${CARRIER_COLOR[c]}" stroke-width="2.4" stroke-linejoin="round" />${dots}`;
    })
    .join('');

  const grid = [0, max / 2, max]
    .map(
      (t) =>
        `<line x1="${P.l}" x2="${W - P.r}" y1="${y(t).toFixed(1)}" y2="${y(t).toFixed(1)}" stroke="var(--border-subtle)" />
         <text x="${P.l - 8}" y="${(y(t) + 4).toFixed(1)}" class="ax" text-anchor="end">${num(t)}</text>`,
    )
    .join('');
  const xl = years.map((yr, i) => `<text x="${x(i).toFixed(1)}" y="${H - 10}" class="ax" text-anchor="middle">${yr}</text>`).join('');

  return `<div class="chart"><svg viewBox="0 0 ${W} ${H}" class="line-chart" role="img" aria-label="Sites per network in ${esc(r.name)} by year">${grid}${xl}${lines}</svg></div>`;
}
